import { useCallback, useMemo } from 'react';

// component
import { ComfyBankingHeader } from 'scbk-shared-module/layout/top-header/ComfyBankingHeader';
import { DesktopHeader } from 'scbk-shared-module/layout/top-header/DesktopHeader';
import { PageMainHeader } from 'scbk-shared-module/layout/top-header/PageMainHeader';
import { SubMainHeader } from 'scbk-shared-module/layout/top-header/SubMainHeader';

// hooks
import {
  deviceUtils,
  Envs,
  i18n,
  useAppBridge,
  useEnvContext,
  useMenuRouter,
  useMenuStore,
} from 'scbk-services';

// types
import type { MainHeaderProps } from 'scbk-shared-module/layout/types/HeaderType';
import type { MenuItem } from 'scbk-shared-module/layout/types/MenuItem';

import { useTranslation } from 'react-i18next';
import {
  Dialog,
  useAdobeDataLayer,
  useChangeMode,
  useGlobalPopupContext,
  useLogout,
} from 'scbk-shared-module';
import { type DarkModeResult } from 'scbk-shared-module/modules/GlobalComponents';
import { HeaderStyles, cn } from 'scbk-ui-web';

const MainHeader = ({
  title,
  isNew = false,
  mainHeaderType = 'subMain',
  isScrolled = false,
  isShadow = true,
  darkMode = false,
  topBar = false,
}: MainHeaderProps) => {
  const isDesktop = deviceUtils.isPC();

  // 다국어처리
  const { t } = useTranslation();

  const { openDarkModePopup, openUserInfoPopup } = useGlobalPopupContext();

  // 로그아웃 Hook
  const { doLogout } = useLogout();

  // 네이티브 기능
  const { doNGetDeviceStorage, doNSetDeviceStorage } = useAppBridge();

  // 편한뱅킹 모드 변환, 다크모드 변환
  const { changeSeniorMode, changeDarkMode } = useChangeMode();

  // Adobe analytics 관련 Hook
  const { push: pushAdobe } = useAdobeDataLayer();

  // 메뉴 라우터
  const { goMenu } = useMenuRouter();

  // menu store : 현재 환경별 xml로 읽어온다.
  const { siteMapTree, menuTree } = useMenuStore.getState();

  // 환경 Context
  const { getEnv } = useEnvContext();

  // 메뉴 object 정제
  const composeMenuItem = useCallback(
    (tree?: Record<string, any>): MenuItem[] => {
      if (!tree) return [];

      const isKo = i18n.language === 'ko';

      return Object.keys(tree)
        .filter((key) => {
          if (isKo) {
            return true;
          }

          // 영어 설정의 경우 영문 메뉴만 filter
          const menuInfo = tree[key];

          return menuInfo.fields?.useEn === 'Y' && menuInfo.fields?.engName;
        })
        .map((key) => {
          const menuInfo = tree[key];

          let menuName = menuInfo?.name ?? '';

          if (!isKo && menuInfo.fields?.useEn === 'Y' && menuInfo.fields?.engName) {
            menuName = menuInfo.fields?.engName;
            if (key === 'SVCSUP000') {
              menuName = 'Lost Report';
            }
          }

          return {
            id: key,
            label: menuName ?? '',
            href: menuInfo?.url ?? '',
            channel: [...(menuInfo?.channel ?? [])],
            subitems: composeMenuItem(menuInfo?.children),
          };
        });
    },
    [i18n.language],
  );

  // 인뱅 메뉴만
  const iBMenuItems = useMemo(() => {
    let tree = { ...siteMapTree['ROOT']?.children };

    if (getEnv() === Envs.LOCAL || getEnv() === Envs.DEV) {
      const sampleMenu = Object.values(menuTree['SAMPLE0000']?.children ?? {}).reduce(
        (acc, item) => {
          acc = { ...acc, ...item?.children };
          return acc;
        },
        {},
      );

      const SAMPLE0000 = {
        ...menuTree['SAMPLE0000'],
        children: { ...sampleMenu },
      };

      tree = { ...tree, SAMPLE0000 };
    }

    return composeMenuItem(tree);
  }, [siteMapTree['ROOT']?.children, i18n.language]);

  // 내알림 이동
  const onHandleAlert = useCallback(() => {
    goMenu('KLDUPD000');
  }, []);

  // 전체 검색으로 이동
  const onHandleSearchPanel = useCallback(() => {
    goMenu('KLDSCH000');
  }, []);

  /**
   * 로그아웃 처리
   */
  const onHandleLogout = useCallback(async () => {
    const isOk = await Dialog.confirm({
      title: '',
      content: t('Layout.MainHeader.001', {
        defaultValue: '로그아웃 할까요?',
      }),
      okBtnLabel: t('Layout.MainHeader.002', {
        defaultValue: '예?',
      }),
      cancelBtnLabel: t('Layout.MainHeader.003', {
        defaultValue: '아니오?',
      }),
    });

    if (!isOk) {
      return;
    }

    doLogout({
      onLogout: async () => {},
    });
  }, []);

  // 편한뱅킹, 다크모드
  const handleSeniorDarkMode = useCallback(async () => {
    const isDarkModeNotice = await doNGetDeviceStorage('CMM', 'DarkModeNotice');

    try {
      if (isDarkModeNotice !== 'N') {
        if (!darkMode) {
          const darkModeChangeResult = (await openDarkModePopup()) as DarkModeResult;

          console.log('### darkModeChangeResult : ', darkModeChangeResult);

          if (darkModeChangeResult.noticeShowYn === 'N') {
            console.log('### noticeShowYn : ', darkModeChangeResult.noticeShowYn);
            await doNSetDeviceStorage('CMM', 'DarkModeNotice', 'N');
          }

          if (darkModeChangeResult.darkChange === 'Y') {
            console.log('### darkChange : ', darkModeChangeResult.darkChange);
            changeDarkMode('Y');
          }
        }
      }
    } finally {
      // adobe hook 호출
      pushAdobe({
        event: 'easy_mode_on',
        includeUserInfo: true,
      });

      changeSeniorMode({
        seniorMode: 'Y',
        dbChkYN: 'Y',
        callback: () => {
          //편한뱅킹 홈으로 이동
          goMenu('KLDCMF000', {
            historyOptions: { clearHistory: true },
          });
        },
      });
    }
  }, [
    changeSeniorMode,
    changeDarkMode,
    pushAdobe,
    goMenu,
    openDarkModePopup,
    doNGetDeviceStorage,
    doNSetDeviceStorage,
  ]);

  /**
   * 편한뱅킹 모드 on/off 여부 처리
   * @param mode 편한뱅킹 모드여부 Y: 편한뱅킹, N: 일반뱅킹
   */
  const onToggleLargeText = useCallback(
    (mode: 'Y' | 'N') => {
      switch (mode) {
        case 'Y':
          handleSeniorDarkMode();
          break;
        case 'N':
          // adobe hook 호출
          pushAdobe({
            event: 'easy_mode_off',
            includeUserInfo: true,
          });

          changeSeniorMode({
            seniorMode: 'N',
            callback: () => {
              //일반뱅킹 홈으로 이동해야함
              goMenu('KLDHOM000', {
                historyOptions: { clearHistory: true },
              });
            },
          });
          break;
        default:
          break;
      }
    },
    [changeSeniorMode, pushAdobe, handleSeniorDarkMode],
  );

  return (
    <>
      {isDesktop ? (
        <DesktopHeader menuitems={iBMenuItems} isScrolled={isScrolled} />
      ) : (
        <>
          {mainHeaderType === 'pageMain' && (
            <div
              className={cn(HeaderStyles['header-wrap'], isScrolled ? HeaderStyles.scrolled : '')}
            >
              <PageMainHeader
                // isScrolled={isScrolled}
                onHandleAlert={onHandleAlert}
                onHandleSearchPanel={onHandleSearchPanel}
                onHandleLogout={onHandleLogout}
                onToggleLargeText={onToggleLargeText}
              />
            </div>
          )}
          {mainHeaderType === 'subMain' && (
            <div
              className={cn(
                HeaderStyles['header-wrap'],
                isScrolled ? HeaderStyles.scrolled : '',
                isShadow ? '' : HeaderStyles['shadow-none'],
              )}
            >
              <SubMainHeader
                title={title ?? ''}
                isNew={isNew}
                isScrolled={isScrolled}
                onHandleAlert={onHandleAlert}
                onHandleSearchPanel={onHandleSearchPanel}
                openUserInfoPopup={openUserInfoPopup}
              />
            </div>
          )}
          {mainHeaderType === 'comfyBanking' && (
            <div
              className={cn(
                HeaderStyles['header-wrap'],
                isScrolled ? HeaderStyles.scrolled : '',
                isShadow ? '' : HeaderStyles['shadow-none'],
              )}
            >
              <ComfyBankingHeader
                title={title}
                isScrolled={isScrolled}
                darkMode={darkMode}
                onToggleLargeText={onToggleLargeText}
                topBar={topBar}
                onHandleAlert={onHandleAlert}
                onHandleSearchPanel={onHandleSearchPanel}
                onHandleLogout={onHandleLogout}
                openUserInfoPopup={openUserInfoPopup}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export { MainHeader };
